import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/**
 * A row in the device card, not a card of its own.
 *
 * It carried its own border, its own tinted ground and a 36px round badge — a panel inside a panel,
 * twice the height of the five buttons it stands next to. Here it is the same 32px as they are: the
 * switch, then what it controls and what state it is in.
 */
/**
 * A width it keeps from the first frame.
 *
 * Its state arrives from a configuration read and then from the websocket, so the caption under the
 * label is empty for a moment and then becomes "Locked while charging" or "Charger is offline" -
 * three very different widths. Without a floor the row grew as each answer landed and shoved the
 * five buttons beside it to the right, twice, while the reader was looking at them.
 */
export const wrapperSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    minHeight: '38px',
    // A floor rather than a fixed width now that it sits in the header: its caption still arrives in
    // two waves and still has three very different lengths, so the space is reserved either way, but
    // the header can give it more when the language needs more.
    minWidth: theme.spacing(210 / 8),
    justifyContent: 'flex-end',
    flexShrink: 0,
};

export const infoSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(10 / 8),
    minWidth: 0,
};

/** The padlock as a mark on the label, not a badge: the switch already says this is a control. */
export const badgeSx: SxProps<Theme> = {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.neutral[500],
    '& svg': {fontSize: '1rem'},
};

export const titleSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    lineHeight: 1.2,
    color: theme.palette.neutral[500],
};

export const captionSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1.2,
    mt: 0.3,
    color: theme.palette.text.primary,
    // Two lines' worth of room, held whether the text has arrived or not: the longest of these
    // captions wraps on a narrow rail, and finding that out later moved everything below it.
    minHeight: '1.2em',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
};

// Keep the Switch in the flow and overlay the spinner so toggling never shifts layout
export const controlSx: SxProps<Theme> = {
    position: 'relative',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
};

export const spinnerSx: SxProps<Theme> = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: '-8px',
    marginLeft: '-8px',
};
